import { MANAGER_IMPACT_HR_ACCESS } from "@domains/manager-impact/shared/managerImpactAccess.ts";
import { initManagerImpactI18next } from "@domains/manager-impact/client/i18n/i18next.ts";
import { initReviewCyclesI18next } from "@domains/reviews/client/i18n/i18next.ts";
import { initEngagementI18next } from "@domains/engagement/client/i18n/i18next.ts";
import { initSegmentsI18next } from "@domains/segments/client/i18n/i18next.ts";
import { GOAL_OWNER_FILTER_QUERY_KEYS } from "@domains/goals/client/components/goal-owner-filters/goalOwnerFilterQueryKeys.ts";
import { ENGAGEMENT_PERIOD_QUERY_KEY, ENGAGEMENT_SCOPE_QUERY_KEY, ENGAGEMENT_SEGMENT_QUERY_KEY } from "@domains/engagement/shared/queryKeys.ts";
import { AdjustmentsHorizontalIcon, ChartBarIcon, ChatBubbleLeftIcon, ChatBubbleLeftRightIcon, FlagIcon, PresentationChartLineIcon, RectangleGroupIcon, RocketLaunchIcon, SignalIcon, StarIcon, TrophyIcon } from "@heroicons/react/24/outline";
import { withFeature } from "@packages/core/access/featureAccess.ts";
import { hasFeature } from "@packages/core/session/features.ts";
import { hasAnyRole } from "@packages/core/session/roles.ts";
import { initScopeI18next } from "@packages/core-client/scope/i18n/i18next.ts";
import type { AppDeferredRegistrationData, AppModuleRegisterFunction } from "@packages/core-client/registration/appModuleRegisterFunction.ts";
import { registerArea } from "@packages/core-client/registration/registerArea.tsx";
import { useFeatureFlags, type FireflyRuntime } from "@squide/firefly";
import type { ReactNode } from "react";
import { Navigate } from "react-router";
import { ANALYTICS_ACCESS, ENGAGEMENT_ANALYTICS_ACCESS, OFFICEVIBE_ANALYTICS_ACCESS, PERFORMANCE_ANALYTICS_ACCESS } from "#shared/access.ts";
import { ANALYTICS_FILTER_QUERY_KEYS } from "./hooks/analyticsFilterQueryKeys.ts";
import { COMPARISON_QUERY_KEYS } from "./hooks/comparisonQueryKeys.ts";
import { FEEDBACK_FILTER_QUERY_KEYS, FEEDBACK_PERIOD_QUERY_KEYS, FEEDBACK_SCOPE_QUERY_KEY, FEEDBACK_SEGMENT_QUERY_KEY } from "./hooks/feedbackFilterQueryKeys.ts";
import { GOOD_VIBES_FILTER_QUERY_KEYS, GOOD_VIBES_SEGMENT_QUERY_KEY } from "./hooks/goodVibesFilterQueryKeys.ts";
import { ONBOARDING_FILTER_QUERY_KEYS } from "./hooks/onboardingFilterQueryKeys.ts";
import { initI18next } from "./i18n/i18next.ts";

const HIDE_EVERY_TAB = () => true;

async function registerMsw(runtime: FireflyRuntime) {
    if (runtime.isMswEnabled) {
        const [{ getRequestHandlers }, { feedbackExportRequestHandlers }] = await Promise.all([import("./api/mocks/getRequestHandlers.ts"), import("./api/mocks/assistant/feedbackExportScript.ts")]);

        runtime.registerRequestHandlers(getRequestHandlers());
        runtime.registerRequestHandlers(feedbackExportRequestHandlers, { prepend: true });
    }
}

interface GoodVibesLandingGuardProps {
    children: ReactNode;
}

function GoodVibesLandingGuard({ children }: GoodVibesLandingGuardProps) {
    const flags = useFeatureFlags();

    if (!flags["good-vibes-analytics"]) {
        return <Navigate to="/" replace />;
    }

    return children;
}

async function withEngagementLandingGuard(page: ReactNode) {
    const { EngagementLandingGuard } = await import("./components/EngagementLandingGuard.tsx");

    return <EngagementLandingGuard>{page}</EngagementLandingGuard>;
}

export const registerAnalytics: AppModuleRegisterFunction = async runtime => {
    const i18nextInstance = initI18next(runtime);
    initReviewCyclesI18next(runtime);
    initScopeI18next(runtime);
    initEngagementI18next(runtime);
    initSegmentsI18next(runtime);
    initManagerImpactI18next(runtime);

    await registerMsw(runtime);

    const area = registerArea(runtime, "analytics", "/analytics", ChartBarIcon, i18nextInstance, { access: ANALYTICS_ACCESS });

    const engagementSection = area.registerSidebarArea("engagement");
    const engagement = engagementSection.registerTabsArea("main", "/engagement", {
        landing: true,
        access: withFeature("Engagement", ENGAGEMENT_ANALYTICS_ACCESS),
        preserveSearchParams: [ENGAGEMENT_SCOPE_QUERY_KEY, ENGAGEMENT_SEGMENT_QUERY_KEY, ENGAGEMENT_PERIOD_QUERY_KEY],
        chrome: async () => (await import("./components/engagementTabsChrome.ts")).ENGAGEMENT_TABS_CHROME
    });

    const engagementOverview = engagement.registerRoute("overview", {
        index: true,
        page: "AnalyticsEngagement",
        lazy: async () => {
            const { EngagementOverviewPage } = await import("./pages/engagement/overview/EngagementOverviewPage.tsx");

            return { element: await withEngagementLandingGuard(<EngagementOverviewPage />) };
        }
    });

    const engagementMetrics = engagement.registerRoute("metrics", {
        path: "/metrics",
        page: "AnalyticsEngagement",
        lazy: async () => {
            const { EngagementMetricsPage } = await import("./pages/engagement/metrics/EngagementMetricsPage.tsx");

            return { element: await withEngagementLandingGuard(<EngagementMetricsPage />) };
        }
    });

    const engagementQuestionResults = engagement.registerRoute("question-results", {
        path: "/question-results",
        page: "AnalyticsEngagement",
        lazy: async () => {
            const { EngagementQuestionResultsPage } = await import("./pages/engagement/question-results/EngagementQuestionResultsPage.tsx");

            return { element: await withEngagementLandingGuard(<EngagementQuestionResultsPage />) };
        }
    });

    const engagementHeatmap = engagement.registerRoute("heatmap", {
        path: "/heatmap",
        page: "AnalyticsEngagement",
        lazy: async () => {
            const { EngagementHeatmapPage } = await import("./pages/engagement/heatmap/EngagementHeatmapPage.tsx");

            return { element: await withEngagementLandingGuard(<EngagementHeatmapPage />) };
        }
    });

    engagementSection.registerRoute("metric-detail", {
        path: "/engagement/metrics/:metricId",
        page: "AnalyticsEngagementMetricDetail",
        noHeading: true,
        access: withFeature("Engagement", ENGAGEMENT_ANALYTICS_ACCESS),
        lazy: async () => {
            const { EngagementMetricDetailPage } = await import("./pages/engagement/metrics/EngagementMetricDetailPage.tsx");
            const { EngagementMetricDetailErrorBoundary } = await import("./pages/engagement/metrics/EngagementMetricDetailErrorBoundary.tsx");

            return { element: await withEngagementLandingGuard(<EngagementMetricDetailPage />), errorElement: <EngagementMetricDetailErrorBoundary /> };
        }
    });

    engagementSection.registerRoute("custom-question-detail", {
        path: "/engagement/question-results/:questionId",
        page: "AnalyticsEngagementCustomQuestionDetail",
        noHeading: true,
        access: withFeature("Engagement", ENGAGEMENT_ANALYTICS_ACCESS),
        lazy: async () => {
            const { CustomQuestionDetailPage } = await import("./pages/engagement/question-results/detail/CustomQuestionDetailPage.tsx");
            const { CustomQuestionDetailErrorBoundary } = await import("./pages/engagement/question-results/detail/CustomQuestionDetailErrorBoundary.tsx");

            return { element: await withEngagementLandingGuard(<CustomQuestionDetailPage />), errorElement: <CustomQuestionDetailErrorBoundary /> };
        }
    });

    const feedback = engagementSection.registerTabsArea("feedback", "/feedback", {
        access: withFeature("Engagement", ENGAGEMENT_ANALYTICS_ACCESS),
        preserveSearchParams: [FEEDBACK_SCOPE_QUERY_KEY, FEEDBACK_SEGMENT_QUERY_KEY, ...FEEDBACK_PERIOD_QUERY_KEYS, ...FEEDBACK_FILTER_QUERY_KEYS],
        chrome: async () => (await import("./components/feedbackTabsChrome.ts")).FEEDBACK_TABS_CHROME
    });

    const feedbackOverview = feedback.registerRoute("overview", {
        index: true,
        page: "AnalyticsFeedback",
        lazy: async () => {
            const { FeedbackOverviewPage } = await import("./pages/feedback/overview/FeedbackOverviewPage.tsx");

            return { element: <FeedbackOverviewPage /> };
        }
    });

    const feedbackReplyRate = feedback.registerRoute("reply-rate", {
        path: "/reply-rate",
        page: "AnalyticsFeedback",
        lazy: async () => {
            const { FeedbackReplyRatePage } = await import("./pages/feedback/reply-rate/FeedbackReplyRatePage.tsx");

            return { element: <FeedbackReplyRatePage /> };
        }
    });

    const feedbackQualitativeTrends = feedback.registerRoute("qualitative-trends", {
        path: "/qualitative-trends",
        page: "AnalyticsFeedback",
        lazy: async () => {
            const { FeedbackQualitativeTrendsPage } = await import("./pages/feedback/qualitative-trends/FeedbackQualitativeTrendsPage.tsx");

            return { element: <FeedbackQualitativeTrendsPage /> };
        }
    });

    engagementSection.registerRoute("trend-detail", {
        path: "/feedback/qualitative-trends/:category",
        page: "AnalyticsFeedbackTrendDetail",
        noHeading: true,
        access: withFeature("Engagement", ENGAGEMENT_ANALYTICS_ACCESS),
        lazy: async () => {
            const { FeedbackTrendDetailPage } = await import("./pages/feedback/qualitative-trends/detail/FeedbackTrendDetailPage.tsx");
            const { FeedbackTrendDetailErrorBoundary } = await import("./pages/feedback/qualitative-trends/detail/FeedbackTrendDetailErrorBoundary.tsx");

            return { element: <FeedbackTrendDetailPage />, errorElement: <FeedbackTrendDetailErrorBoundary /> };
        }
    });

    const performanceSection = area.registerSidebarArea("performance");
    const performance = performanceSection.registerTabsArea("main", "/performance", {
        landing: true,
        access: withFeature("PerformanceReviews", PERFORMANCE_ANALYTICS_ACCESS),
        preserveSearchParams: [...ANALYTICS_FILTER_QUERY_KEYS, ...COMPARISON_QUERY_KEYS],
        chrome: async () => (await import("./components/performanceTabsChrome.ts")).PERFORMANCE_TABS_CHROME
    });

    const performanceOverview = performance.registerRoute("overview", {
        index: true,
        page: "PerformanceAnalytics",
        lazy: async () => {
            const { PerformanceOverviewPage } = await import("./pages/performance/overview/PerformanceOverviewPage.tsx");

            return { element: <PerformanceOverviewPage /> };
        }
    });

    const performanceCalibration = performance.registerRoute("calibration", {
        path: "/calibration",
        page: "PerformanceAnalytics",
        lazy: async () => {
            const { PerformanceCalibrationPage } = await import("./pages/performance/calibration/PerformanceCalibrationPage.tsx");

            return { element: <PerformanceCalibrationPage /> };
        }
    });

    const performanceThemes = performance.registerRoute("themes", {
        path: "/themes",
        page: "PerformanceAnalytics",
        lazy: async () => {
            const { PerformanceThemesPage } = await import("./pages/performance/themes/PerformanceThemesPage.tsx");

            return { element: <PerformanceThemesPage /> };
        }
    });

    const lifecycleSection = area.registerSidebarArea("lifecycle");
    const onboarding = lifecycleSection.registerTabsArea("onboarding", "/onboarding", {
        access: withFeature("Engagement", ENGAGEMENT_ANALYTICS_ACCESS),
        preserveSearchParams: ONBOARDING_FILTER_QUERY_KEYS,
        chrome: async () => (await import("./components/onboardingTabsChrome.ts")).ONBOARDING_TABS_CHROME
    });

    const onboardingReport = onboarding.registerRoute("report", {
        index: true,
        page: "AnalyticsOnboarding",
        lazy: async () => {
            const { OnboardingPage } = await import("./pages/onboarding/OnboardingPage.tsx");

            return { element: <OnboardingPage /> };
        }
    });

    const recognitionSection = area.registerSidebarArea("recognition");
    const goodVibes = recognitionSection.registerTabsArea("main", "/good-vibes", {
        landing: true,
        access: withFeature("Recognition", OFFICEVIBE_ANALYTICS_ACCESS),
        preserveSearchParams: [...GOOD_VIBES_FILTER_QUERY_KEYS, GOOD_VIBES_SEGMENT_QUERY_KEY],
        chrome: async () => (await import("./components/goodVibesTabsChrome.ts")).GOOD_VIBES_TABS_CHROME
    });

    const goodVibesOverview = goodVibes.registerRoute("overview", {
        index: true,
        page: "AnalyticsGoodVibes",
        lazy: async () => {
            const { GoodVibesOverviewPage } = await import("./pages/good-vibes/GoodVibesOverviewPage.tsx");

            return {
                element: (
                    <GoodVibesLandingGuard>
                        <GoodVibesOverviewPage />
                    </GoodVibesLandingGuard>
                )
            };
        }
    });

    const goodVibesLeaderboard = goodVibes.registerRoute("leaderboard", {
        path: "/leaderboard",
        page: "AnalyticsGoodVibes",
        lazy: async () => {
            const { GoodVibesLeaderboardPage } = await import("./pages/good-vibes/GoodVibesLeaderboardPage.tsx");

            return {
                element: (
                    <GoodVibesLandingGuard>
                        <GoodVibesLeaderboardPage />
                    </GoodVibesLandingGuard>
                )
            };
        }
    });

    const goodVibesCollections = goodVibes.registerRoute("collections", {
        path: "/collections",
        page: "AnalyticsGoodVibes",
        lazy: async () => {
            const { GoodVibesCollectionsPage } = await import("./pages/good-vibes/GoodVibesCollectionsPage.tsx");

            return {
                element: (
                    <GoodVibesLandingGuard>
                        <GoodVibesCollectionsPage />
                    </GoodVibesLandingGuard>
                )
            };
        }
    });

    const goalUsage = performanceSection.registerTabsArea("goals", "/goals", {
        landing: true,
        access: withFeature("Goals", PERFORMANCE_ANALYTICS_ACCESS),
        preserveSearchParams: GOAL_OWNER_FILTER_QUERY_KEYS,
        chrome: async () => ({ useIsTabHidden: () => HIDE_EVERY_TAB })
    });

    const goalUsageReport = goalUsage.registerRoute("usage", {
        index: true,
        page: "AnalyticsGoalUsage",
        lazy: async () => {
            const { GoalUsageReportPage } = await import("./pages/goals/usage/GoalUsageReportPage.tsx");

            return { element: <GoalUsageReportPage /> };
        }
    });

    const managerImpact = engagementSection.registerTabsArea("manager-impact", "/manager-impact", { landing: true, access: MANAGER_IMPACT_HR_ACCESS, hideTitle: true, chrome: async () => ({ useRenderAsTitleOnly: () => true }) });

    const managerImpactOverview = managerImpact.registerRoute("overview", {
        index: true,
        lazy: async () => {
            const { ManagerImpactHrPage } = await import("./pages/manager-impact/ManagerImpactHrPage.tsx");

            return { element: <ManagerImpactHrPage /> };
        }
    });

    engagementSection.registerRoute("manager-impact-report", {
        path: "/manager-impact/:managerId",
        noHeading: true,
        access: MANAGER_IMPACT_HR_ACCESS,
        lazy: async () => {
            const { ManagerImpactManagerReportPage } = await import("./pages/manager-impact/ManagerImpactManagerReportPage.tsx");

            return { element: <ManagerImpactManagerReportPage /> };
        }
    });

    return (deferredRuntime: FireflyRuntime, { session }: AppDeferredRegistrationData) => {
        if (!hasAnyRole(session, ANALYTICS_ACCESS)) {
            return;
        }

        const isOfficevibeExecutive = hasAnyRole(session, OFFICEVIBE_ANALYTICS_ACCESS);
        const isPerformanceExecutive = hasAnyRole(session, PERFORMANCE_ANALYTICS_ACCESS);
        const isEngagementViewer = isOfficevibeExecutive || (hasAnyRole(session, ENGAGEMENT_ANALYTICS_ACCESS) && deferredRuntime.getFeatureFlag("engagement-analytics-for-collaborator", false));
        const hasEngagement = isEngagementViewer && hasFeature("Engagement")(session);
        const showsEngagement = hasEngagement && deferredRuntime.getFeatureFlag("engagement-analytics", false);
        const showsFeedback = hasEngagement && deferredRuntime.getFeatureFlag("feedback-analytics", false);
        const showsOnboarding = hasEngagement && deferredRuntime.getFeatureFlag("onboarding-analytics-v1", false);
        const showsPerformance = isPerformanceExecutive && hasFeature("PerformanceReviews")(session);
        const showsGoals = isPerformanceExecutive && hasFeature("Goals")(session);
        const showsManagerImpact = hasAnyRole(session, MANAGER_IMPACT_HR_ACCESS);
        const showsGoodVibes = isOfficevibeExecutive && deferredRuntime.getFeatureFlag("good-vibes-analytics", false) && hasFeature("Recognition")(session);

        if (showsEngagement) {
            engagement.registerLink({ priority: 690, icon: SignalIcon });
            engagementOverview.registerLink({ priority: 1000 });
            engagementMetrics.registerLink({ priority: 999 });
            engagementQuestionResults.registerLink({ priority: 998 });
            engagementHeatmap.registerLink({ priority: 997 });
        }

        if (showsFeedback) {
            feedback.registerLink({ priority: 640, icon: ChatBubbleLeftIcon });
            feedbackOverview.registerLink({ priority: 1000 });

            if (deferredRuntime.getFeatureFlag("feedback-analytics-response-rate-v1", false)) {
                feedbackReplyRate.registerLink({ priority: 999 });
            }

            if (deferredRuntime.getFeatureFlag("feedback-analytics-qualitative-trends-v1", false)) {
                feedbackQualitativeTrends.registerLink({ priority: 998 });
            }
        }

        if (showsPerformance) {
            performance.registerLink({ priority: 590, icon: StarIcon });
            performanceOverview.registerLink({ priority: 1000, icon: RectangleGroupIcon });
            performanceCalibration.registerLink({ priority: 999, icon: AdjustmentsHorizontalIcon });
            performanceThemes.registerLink({ priority: 998, icon: ChatBubbleLeftRightIcon });
        }

        if (showsGoals) {
            goalUsage.registerLink({ priority: 540, icon: FlagIcon });
            goalUsageReport.registerLink({ priority: 1000 });
        }

        if (showsManagerImpact) {
            managerImpact.registerLink({ priority: 490, icon: PresentationChartLineIcon });
            managerImpactOverview.registerLink({ priority: 1000 });
        }

        if (showsOnboarding) {
            onboarding.registerLink({ priority: 465, icon: RocketLaunchIcon });
            onboardingReport.registerLink({ priority: 1000 });
        }

        if (showsGoodVibes) {
            goodVibes.registerLink({ priority: 440, icon: TrophyIcon });
            goodVibesOverview.registerLink({ priority: 1000 });

            if (deferredRuntime.getFeatureFlag("good-vibes-analytics-v1", false)) {
                goodVibesLeaderboard.registerLink({ priority: 999 });
                goodVibesCollections.registerLink({ priority: 998 });
            }
        }

        if (showsEngagement || showsFeedback || showsPerformance || showsGoals || showsManagerImpact || showsOnboarding || showsGoodVibes) {
            area.registerToTopNav({ priority: 100 });
        }
    };
};
